import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Box,
  Grid,
  Divider,
} from '@mui/material';
import {
  LocationOn,
  Event,
  Group,
  AccessTime,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { Activity } from '../types/Activity';
import SafeHtmlRenderer from './SafeHtmlRenderer';
import ParticipantsPreviewStack from './ParticipantsPreviewStack';
import { formatActivityDate, formatTime } from '../utils/dateUtils';
import { formatParticipantsLabel } from '../utils/participantUtils';
import ActivityDateBadge from './ActivityDateBadge';

interface ActivityCardProps {
  activity: Activity;
  onActivityClick?: (activity: Activity) => void;
}

const ActivityCard: React.FC<ActivityCardProps> = ({ activity, onActivityClick }) => {
  const [imageError, setImageError] = useState(false);


  const handleCardClick = () => {
    if (onActivityClick) {
      onActivityClick(activity);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick();
    }
  };

  const handleImageError = () => {
    console.warn(`🖼️ Image failed to load for activity ${activity.id}:`, activity.image_url);
    setImageError(true);
  };

  // Check if image should be shown based on URL validity and CORS considerations
  const shouldShowImage = () => {
    if (!activity.image_url || imageError) return false;
    
    // Allow Firebase Storage URLs (our legitimate image storage)
    if (activity.image_url.includes('firebasestorage.googleapis.com')) {
      return true;
    }
    
    // Block known problematic external services to prevent CORS issues
    if (activity.image_url.includes('unsplash.com') || 
        activity.image_url.includes('source.unsplash')) {
      return false;
    }
    
    // Allow relative URLs and same-domain URLs
    if (!activity.image_url.startsWith('http')) {
      return true;
    }
    
    // Block other external domains to prevent CORS issues
    return activity.image_url.includes(window.location.hostname);
  };

  const participantsLabel = formatParticipantsLabel(
    activity.current_participants,
    activity.max_participants
  );

  return (
    <Card 
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      // WCAG 2.1.1 & 4.1.2 - Keyboard navigation and accessibility
      tabIndex={onActivityClick ? 0 : -1}
      role={onActivityClick ? 'button' : 'article'}
      aria-label={`Activity: ${activity.title}. ${activity.description.replace(/<[^>]*>/g, '').substring(0, 100)}... Click to view details.`}
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        cursor: onActivityClick ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
        // WCAG 2.4.7 - Enhanced focus indicators
        '&:focus-visible': {
          outline: '3px solid #005FCC',
          outlineOffset: '2px',
        },
        // Reduced motion support
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:hover': {
            transform: 'none',
          },
        },
      }}
    >
      {shouldShowImage() && (
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            height="200"
            image={activity.image_url}
            alt={`${activity.title} - ${activity.category.name} activity in ${activity.city}`}
            onError={handleImageError}
            sx={{ objectFit: 'cover' }}
          />
          <ActivityDateBadge
            date={activity.date}
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
            }}
          />
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Typography gutterBottom variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
          {activity.title}
        </Typography>
        
        <SafeHtmlRenderer
          htmlContent={activity.description}
          variant="body2"
          color="text.secondary"
          maxLength={150}
          showEllipsis={true}
          sx={{ 
            mb: 2, 
            display: '-webkit-box', 
            WebkitLineClamp: 3, 
            WebkitBoxOrient: 'vertical', 
            overflow: 'hidden' 
          }}
        />

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Event sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {formatActivityDate(activity)}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTime sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
              <Typography variant="body2">
                {formatTime(activity.time)}
                {activity.end_time && ` - ${formatTime(activity.end_time)}`}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocationOn sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {activity.location_name}, {activity.city}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Group sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
              <Typography variant="body2">
                {participantsLabel}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <ParticipantsPreviewStack
          participants={activity.participants_preview}
          totalCount={activity.current_participants}
          avatarSize={34}
          sx={{ mt: 1 }}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip
            icon={<CategoryIcon />}
            label={activity.category.name}
            size="small"
            color="primary"
            variant="outlined"
          />
          {activity.tags && Array.isArray(activity.tags) && activity.tags.slice(0, 2).map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              size="small"
              variant="outlined"
            />
          ))}
          {activity.tags && activity.tags.length > 2 && (
            <Chip
              label={`+${activity.tags.length - 2} more`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            by {activity.organizer_name}
          </Typography>
          <Chip
            label={activity.skill_level}
            size="small"
            variant="filled"
            sx={{
              // WCAG AA compliant colors
              backgroundColor: '#2e7d32', // Dark green for better contrast
              color: '#ffffff', // White text
              fontWeight: 600,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ActivityCard; 